// eslint-disable-next-line import/no-cycle
import { getCurrentInstance, reactive, watch, ref } from 'vue'
import { miniStore } from '@/miniStore'

// 调用refreshBack
export const useRefreshBack = () => {
  const vm = getCurrentInstance()
  if (!vm) throw new Error('must be called in setup')
  return vm.proxy.$refreshBack.bind(vm.proxy)
}
// 访问vuex
export const useStore = () => {
  const vm = getCurrentInstance()
  if (!vm) throw new Error('must be called in setup')
  return vm.proxy.$store
}

// 访问router
export const useRouter = () => {
  const vm = getCurrentInstance()
  if (!vm) throw new Error('must be called in setup')
  return vm.proxy.$router
}

// 访问route
export const useRoute = () => {
  const vm = getCurrentInstance()
  if (!vm) throw new Error('must be called in setup')
  return vm.proxy.$route
}

// 判断访问权限
export const usePermission = () => {
  const store = useStore()
  const allResource = store.getters.allResource || []
  return (key) => {
    return allResource.includes(key)
  }
}

/**
   * 字典转列表，用于select下拉列表等场景
   * @param {*} obj  Object 原对象 JSON格式
   * @param 第二个参数为选填 不填默认解析JSON的keys生成数组，对应value,label
   *第二个参数支持两种情况
    1. Boolean值 默认会在JSON解析基础后数组，添加数组第一项，默认{value:'',label:'全部'}
    2. JSON对象 若第一项需要自定义，可直接传递对象进行解析，如{value:-1,label:'全部车辆'}
*/
export const useDictParseList = (obj, param2) => {
  const keys = Object.keys(obj)
  const arr = []
  for (let i = 0; i < keys.length; i++) {
    arr.push({
      value: parseInt(keys[i], 10),
      label: obj[keys[i]],
    })
  }
  if (param2) {
    const type = (typeof param2).toUpperCase()
    if (type === 'BOOLEAN') {
      arr.unshift({
        value: '',
        label: '全部',
      })
    } else if (param2 instanceof Object) {
      if (Object.keys(param2).length) {
        arr.unshift({
          value: param2.value,
          label: param2.label,
        })
      }
    }
  }
  return arr
}

// 使用 [] 包裹promise
export const use$to = () => {
  return function (promise) {
    return promise.then((data) => [null, data]).catch((err) => [err])
  }
}

//  Vue.prototype.$EventBus = EventBus
export const useEventBus = () => {
  const vm = getCurrentInstance()
  if (!vm) throw new Error('must be called in setup')
  return vm.proxy.$EventBus
}

/**
 * 把 el-form 与 MiniStore 双向打通
 * @param {string} stepKey   对应 store 里的 step0 / step1 / step2
 * @param {Object} rules     Element 校验规则（可选）
 */
export function useFormSync(stepKey, defaultValue = {}) {
  const { proxy } = getCurrentInstance() // 拿到 vue 实例，后面要 $refs.form

  /* 1. 本地 reactive 模型（给 el-form 用） */
  // 优先使用 store 中的数据，如果 store 中没有数据则使用 defaultValue
  const storeData = miniStore.state.data[stepKey]
  const initialData = Object.keys(storeData).length > 0 ? storeData : defaultValue
  const formModel = reactive({ ...initialData })

  let isUpdatingFromStore = false

  /* 2. 值一变就同步回 MiniStore */
  watch(
    () => formModel,
    () => {
      // 如果正在从 store 更新，跳过同步回 store
      if (isUpdatingFromStore) return
      Object.keys(formModel).forEach((fieldKey) => {
        miniStore.dispatch({
          type: 'FIELD_CHANGE',
          payload: { stepKey, fieldKey, value: formModel[fieldKey] },
        })
      })
    },
    { deep: true, immediate: true },
  )

  /* 3. Store 变化时同步回本地 formModel */
  watch(
    () => miniStore.state.data[stepKey],
    (newData) => {
      isUpdatingFromStore = true
      Object.keys(newData).forEach((k) => {
        const v = newData[k]
        formModel[k] = Array.isArray(v) ? v.slice() : v
      })
      // 使用 nextTick 确保所有更新完成后再重置标志
      proxy.$nextTick(() => {
        isUpdatingFromStore = false
      })
    },
    { deep: true, immediate: true },
  )

  /* 暴露给模板：统一校验函数 */
  const validate = () => proxy.$refs.form.validate()

  return {
    formModel, // v-model 直接绑这个
    validate, // 提交前调用
  }
}

export const useFixedBottomBar = (options = {}) => {
  const { containerId = 'app-main', horizontalPadding = 16, bottom = 0 } = options
  const fixedStyle = ref({})

  const update = () => {
    const container = document.getElementById(containerId)
    if (container) {
      const rect = container.getBoundingClientRect()
      fixedStyle.value = {
        position: 'fixed',
        left: `${rect.left + horizontalPadding}px`,
        width: `${rect.width - horizontalPadding * 2}px`,
        bottom: `${bottom}px`,
        background: '#fff',
        zIndex: 999,
      }
    } else {
      fixedStyle.value = {}
    }
  }

  const initFixedBottomBar = () => {
    update()
    window.addEventListener('resize', update)
  }

  const destroyFixedBottomBar = () => {
    window.removeEventListener('resize', update)
  }

  return {
    fixedStyle,
    updateFixedStyle: update,
    initFixedBottomBar,
    destroyFixedBottomBar,
  }
}
